import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Eye, FileText } from "@phosphor-icons/react";
import PageHeader from "@/components/PageHeader";
import CandidateDetailDialog from "@/components/CandidateDetailDialog";
import EmployeeDetailDialog from "@/components/EmployeeDetailDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TestsPage() {
  const [candTests, setCandTests] = useState([]);
  const [empTests, setEmpTests] = useState([]);
  const [positions, setPositions] = useState([]);
  const [viewCandId, setViewCandId] = useState(null);
  const [viewEmpId, setViewEmpId] = useState(null);
  const [posFilter, setPosFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");

  const load = () => {
    axios.get(`${API}/tests/candidates`, { withCredentials: true }).then(r => setCandTests(r.data)).catch(() => {});
    axios.get(`${API}/tests/employees`, { withCredentials: true }).then(r => setEmpTests(r.data)).catch(() => {});
    axios.get(`${API}/positions`, { withCredentials: true }).then(r => setPositions(r.data)).catch(() => {});
  };
  const { activeWorkspaceId } = useAuth();
  // Batch 62.A: re-load on workspace switch for agency accounts
  useEffect(() => { load(); }, [activeWorkspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const statusColors = { "Completato": "bg-green-50 text-green-700", "In corso": "bg-blue-50 text-blue-700", "In attesa": "bg-yellow-50 text-yellow-700" };

  return (
    <div className="animate-fade-in" data-testid="tests-page">
      <PageHeader title="Analisi" subtitle={`${candTests.length + empTests.length} analisi totali`} />
      <Tabs defaultValue="candidati">
        <TabsList className="bg-white border border-[#0D1F3C]/8 p-1 rounded-lg mb-6">
          <TabsTrigger data-testid="tab-test-candidates" value="candidati" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-6">Candidati ({candTests.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-test-employees" value="interni" className="data-[state=active]:bg-[#1B4FD8] data-[state=active]:text-white rounded-md px-6">Dipendenti ({empTests.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="candidati">
          <div className="flex gap-3 mb-4">
            <Select value={posFilter} onValueChange={setPosFilter}>
              <SelectTrigger className="w-[200px] h-9"><SelectValue placeholder="Tutte le posizioni" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutte le posizioni</SelectItem>{positions.map(p=><SelectItem key={p.id} value={p.title}>{p.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Card className="border-[#0D1F3C]/8">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Candidato</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Posizione</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 w-[60px]">CV</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 w-[80px]">Tipo</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 w-[100px]">Stato</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 w-[100px]">Data</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 text-right w-[80px]">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {candTests.filter(t => !posFilter || posFilter === "all" || t.position_name === posFilter).length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#0D1F3C]/40 text-sm">Nessuna analisi candidato</TableCell></TableRow> :
                  candTests.filter(t => !posFilter || posFilter === "all" || t.position_name === posFilter).map(t => (
                    <TableRow key={t.id} data-testid={`cand-test-${t.id}`}>
                      <TableCell>
                        <button onClick={() => setViewCandId(t.candidate_id)} className="text-left group">
                          <span className="text-sm font-medium text-[#0D1F3C] group-hover:text-[#1B4FD8] transition-colors">{t.candidate_name || '—'}</span>
                        </button>
                      </TableCell>
                      <TableCell className="text-sm text-[#0D1F3C]/60">{t.position_name || '—'}</TableCell>
                      <TableCell>
                        {t.has_cv ? <FileText size={16} weight="duotone" className="text-[#1B4FD8]" /> : <span className="text-[#0D1F3C]/20">—</span>}
                      </TableCell>
                      <TableCell className="text-xs text-[#0D1F3C]/60">{t.test_type || '—'}</TableCell>
                      <TableCell><Badge className={`text-xs ${statusColors[t.status]||''}`}>{t.status}</Badge></TableCell>
                      <TableCell className="text-sm text-[#0D1F3C]/60">{t.completed_at ? new Date(t.completed_at).toLocaleDateString('it-IT') : '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setViewCandId(t.candidate_id)} title="Visualizza scheda completa">
                          <Eye size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="interni">
          <div className="flex gap-3 mb-4">
            <Select value={deptFilter} onValueChange={setDeptFilter}>
              <SelectTrigger className="w-[200px] h-9"><SelectValue placeholder="Tutti i reparti" /></SelectTrigger>
              <SelectContent><SelectItem value="all">Tutti i reparti</SelectItem>{[...new Set(empTests.map(t=>t.department).filter(Boolean))].map(d=><SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Card className="border-[#0D1F3C]/8">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Dipendente</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Ruolo</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50">Reparto</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 w-[80px]">Tipo</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 w-[100px]">Stato</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 w-[100px]">Data</TableHead>
                <TableHead className="text-xs uppercase text-[#0D1F3C]/50 text-right w-[80px]">Azioni</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {empTests.filter(t => !deptFilter || deptFilter === "all" || t.department === deptFilter).length === 0 ? <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#0D1F3C]/40 text-sm">Nessuna analisi dipendente</TableCell></TableRow> :
                  empTests.filter(t => !deptFilter || deptFilter === "all" || t.department === deptFilter).map(t => (
                    <TableRow key={t.id} data-testid={`emp-test-${t.id}`}>
                      <TableCell>
                        <button onClick={() => setViewEmpId(t.employee_id)} className="text-left group">
                          <span className="text-sm font-medium text-[#0D1F3C] group-hover:text-[#1B4FD8] transition-colors">{t.employee_name || '—'}</span>
                        </button>
                      </TableCell>
                      <TableCell className="text-sm text-[#0D1F3C]/60">{t.job_title || '—'}</TableCell>
                      <TableCell className="text-sm text-[#0D1F3C]/60">{t.department || '—'}</TableCell>
                      <TableCell className="text-xs text-[#0D1F3C]/60">{t.test_type || '—'}</TableCell>
                      <TableCell><Badge className={`text-xs ${statusColors[t.status]||''}`}>{t.status}</Badge></TableCell>
                      <TableCell className="text-sm text-[#0D1F3C]/60">{t.completed_at ? new Date(t.completed_at).toLocaleDateString('it-IT') : '—'}</TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm" onClick={() => setViewEmpId(t.employee_id)} title="Visualizza scheda completa">
                          <Eye size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>

      <CandidateDetailDialog candId={viewCandId} open={!!viewCandId} onClose={() => setViewCandId(null)} onRefresh={load} positions={positions} />
      <EmployeeDetailDialog empId={viewEmpId} open={!!viewEmpId} onClose={() => setViewEmpId(null)} onRefresh={load} />
    </div>
  );
}
